<script setup>
import { ref, computed, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/store/user-store';
import ClientInfoForm from '@/components/landing/ClientInfoForm.vue';
import RiskQuestionnaire from '@/components/landing/RiskQuestionnaire.vue';
import AssetAllocationResult from '@/components/landing/AssetAllocationResult.vue';
import AnLoader from '@/components/common/AnLoader.vue';
import { generateDummyAllocation } from '@/utils/dummy-allocation-generator';

const router = useRouter();
const store = useUserStore();

// Provide setAuth function to child components (for the Sign Up button in disclaimer)
const setAuth = (show, view) => {
  // Navigate back to home page with auth modal state
  router.push({
    path: '/',
    query: { openAuth: 'true', authView: view }
  })
}

provide('setAuth', setAuth)

// Page state
const currentStep = ref('clientInfo'); // 'clientInfo' | 'questionnaire' | 'results'
const isSubmitted = ref(false);
const allocationData = ref(null);
const clientData = ref(null);

// Computed properties
const isLoading = computed(() => store.isRiskAssessmentLoading);

// Handle client info submission
const handleClientInfoSubmit = (clientInfo) => {
  clientData.value = clientInfo;
  currentStep.value = 'questionnaire';

  // Scroll to top when moving to next step
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};

// Handle questionnaire submission
const handleQuestionnaireSubmit = async (answersData) => {
  try {
    // Transform answers into the format expected by the API
    const answers = [];
    let totalScore = 0;

    // Process each answer and calculate total score
    Object.entries(answersData).forEach(([questionId, selectedOption]) => {
      answers.push({
        questionId,
        selectedValue: selectedOption.value,
        score: selectedOption.score
      });
      totalScore += selectedOption.score;
    });

    // Calculate risk profile based on total score
    let riskProfile;
    if (totalScore >= 6 && totalScore <= 10) {
      riskProfile = 'Conservative';
    } else if (totalScore >= 11 && totalScore <= 15) {
      riskProfile = 'Moderate';
    } else if (totalScore >= 16 && totalScore <= 20) {
      riskProfile = 'Aggressive';
    } else {
      riskProfile = totalScore <= 10 ? 'Conservative' : totalScore <= 15 ? 'Moderate' : 'Aggressive';
    }

    // Check if this is demo mode (landing page) or authenticated mode (dashboard)
    const isDashboardMode = store.user && store.dashboardData?.customerId

    if (!isDashboardMode) {
      // DEMO MODE: Landing page - use dummy allocation
      console.log('Demo mode - generating illustrative allocation for', riskProfile)
      const dummyAllocation = generateDummyAllocation(riskProfile)

      console.log('Generated dummy allocation:', dummyAllocation)

      // Set the allocation data
      allocationData.value = dummyAllocation

      // Show the results view by changing step
      currentStep.value = 'results'
      isSubmitted.value = true

      // Show success message
      store.fireAlert('Success', 'Risk assessment completed! Below is an illustrative portfolio example.')

      // Scroll to top when showing results
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)

      // Don't proceed further - no API call
      return
    }

    // AUTHENTICATED MODE: Dashboard - call real API
    console.log('Authenticated mode - calling real API for', riskProfile);

    // Make actual API call with risk aversion coefficient
    const result = await store.getRiskAssessment({
      answers: answers,
      totalScore: totalScore,
      riskAversionCoefficient: riskAversionCoefficient
    });

    console.log('API Response:', result);

    // Process API response and transform to UI format
    if (result && (result.assetAllocation || hasApiAllocationData(result))) {
      // If result has assetAllocation property, use it directly (existing format)
      if (result.assetAllocation) {
        allocationData.value = result;
      } else {
        // Transform API response format to UI format
        console.log('Transforming API response to UI format');
        const transformedResult = transformApiResponse(result, riskProfile, answers, totalScore, riskAversionCoefficient);
        console.log('Transformed Result:', transformedResult);
        allocationData.value = transformedResult;
      }
    } else {
      // Fallback to mock data if API doesn't return allocation
      const mockResult = generateMockAllocation(riskProfile, answers, totalScore, riskAversionCoefficient);
      allocationData.value = mockResult;
    }

    currentStep.value = 'results';

    // Scroll to top when showing results
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } catch (error) {
    console.error('Error submitting risk assessment:', error);
    isSubmitted.value = false;
    // Handle error (show notification, etc.)
  }
};

// Calculate risk aversion coefficient based on aggregated score
const calculateRiskAversionCoefficient = (totalScore) => {
  if (totalScore >= 6 && totalScore <= 10) return 6;
  if (totalScore >= 11 && totalScore <= 15) return 4;
  if (totalScore >= 16 && totalScore <= 20) return 2;
  // Fallback for edge cases
  return totalScore <= 10 ? 6 : totalScore <= 15 ? 4 : 2;
};

// Calculate risk profile based on total score
const calculateRiskProfile = (totalScore) => {
  if (totalScore <= 10) return 'Conservative';
  if (totalScore <= 16) return 'Moderate';
  return 'Aggressive';
};

// Check if API response contains allocation data (based on expected keys from API)
const hasApiAllocationData = (result) => {
  const expectedKeys = ['Large-cap Eq.', 'Mid-cap Eq.', 'Small-cap Eq.', 'Fixed Deposits', 'Gold-SGB', 'Flexicap MF', 'Debt MF', 'Real Estate'];
  return expectedKeys.some(key => result.hasOwnProperty(key));
};

// Helper function to normalize allocations to 100% with integer values
const normalizeAllocations = (allocations) => {
  // Convert to numbers and calculate total
  const numericAllocations = allocations.map(item => ({
    ...item,
    numericValue: parseFloat(item.allocation)
  }));

  const total = numericAllocations.reduce((sum, item) => sum + item.numericValue, 0);

  if (total === 0) return allocations; // Avoid division by zero

  // Normalize to 100% and round to integers
  let normalizedAllocations = numericAllocations.map(item => ({
    ...item,
    normalizedValue: (item.numericValue / total) * 100,
    allocation: Math.round((item.numericValue / total) * 100) // Round to nearest integer
  }));

  // Calculate the sum after rounding
  let currentSum = normalizedAllocations.reduce((sum, item) => sum + item.allocation, 0);

  // Adjust for rounding errors to ensure exactly 100%
  const difference = 100 - currentSum;

  if (difference !== 0) {
    // Sort by the fractional part (how much we rounded) to distribute adjustments fairly
    const sortedByRemainder = normalizedAllocations
      .map((item, index) => ({
        ...item,
        index,
        remainder: item.normalizedValue - Math.floor(item.normalizedValue)
      }))
      .sort((a, b) => {
        if (difference > 0) {
          // If we need to add, prioritize items with largest remainders
          return b.remainder - a.remainder;
        } else {
          // If we need to subtract, prioritize items with smallest remainders
          return a.remainder - b.remainder;
        }
      });

    // Distribute the difference
    let remainingAdjustment = Math.abs(difference);
    for (let i = 0; i < sortedByRemainder.length && remainingAdjustment > 0; i++) {
      const item = sortedByRemainder[i];
      const originalIndex = item.index;

      if (difference > 0) {
        // Add 1 to this allocation
        normalizedAllocations[originalIndex].allocation += 1;
        remainingAdjustment--;
      } else {
        // Subtract 1 from this allocation (but don't go below 0)
        if (normalizedAllocations[originalIndex].allocation > 0) {
          normalizedAllocations[originalIndex].allocation -= 1;
          remainingAdjustment--;
        }
      }
    }
  }

  // Remove helper properties and format final allocations as integers
  return normalizedAllocations.map(item => ({
    name: item.name,
    allocation: item.allocation.toString(), // Convert to string without decimals
    risk: item.risk,
    description: item.description
  }));
};

// Transform API response to UI format
const transformApiResponse = (apiResult, riskProfile, answers, totalScore, riskAversionCoefficient) => {
  // Mapping from API keys to display names and risk levels
  const assetMapping = {
    'Large-cap Eq.': {
      name: 'Large-Cap Equity',
      risk: 'Low',
      description: 'Top 100 companies by market cap per SEBI, like Reliance, TCS, and Infosys offering stability.'
    },
    'Mid-cap Eq.': {
      name: 'Mid-Cap Equity',
      risk: 'Medium',
      description: 'Companies ranked 101 to 250 by market capitalisation, local and global leaders with higher growth potential.'
    },
    'Small-cap Eq.': {
      name: 'Small-Cap Equity',
      risk: 'High',
      description: 'Companies ranked below 250 by market capitalisation with high growth potential but higher volatility.'
    },
    'Fixed Deposits': {
      name: 'Fixed Deposits',
      risk: 'Low',
      description: 'Secure fixed-income instruments with guaranteed returns from banks and NBFCs.'
    },
    'Gold-SGB': {
      name: 'Gold',
      risk: 'Low',
      description: 'Precious metal investments for portfolio diversification and inflation protection.'
    },
    'Flexicap MF': {
      name: 'Flexi-cap Mutual Funds',
      risk: 'Medium',
      description: 'Equity funds that invest across large-cap, mid-cap, and small-cap stocks without any restriction.'
    },
    'Debt MF': {
      name: 'Debt Mutual Funds',
      risk: 'Low',
      description: 'Invest in fixed-income instruments to offer stable returns with lower risk than equity funds.'
    },
    'Real Estate': {
      name: 'Real Estate',
      risk: 'Medium',
      description: 'Property investments including REITs for long-term appreciation and rental income.'
    }
  };

  // Transform API response to UI format
  // Note: API returns percentage values (e.g., 29.6 means 29.6%)
  const rawAllocations = [];

  Object.keys(apiResult).forEach(key => {
    if (assetMapping[key] && apiResult[key] > 0) {
      rawAllocations.push({
        name: assetMapping[key].name,
        allocation: apiResult[key], // Keep as raw number for normalization
        risk: assetMapping[key].risk,
        description: assetMapping[key].description
      });
    }
  });

  // Normalize allocations to ensure they sum to 100%
  const assetAllocation = normalizeAllocations(rawAllocations);

  // Sort by allocation percentage (highest first)
  assetAllocation.sort((a, b) => parseFloat(b.allocation) - parseFloat(a.allocation));

  const investmentHorizon = getInvestmentHorizon(answers);
  const expectedReturn = getExpectedReturn(riskProfile);

  return {
    clientName: clientData.value?.name || 'Client',
    clientEmail: clientData.value?.email || '',
    clientAge: clientData.value?.age || '',
    riskProfile,
    investmentHorizon,
    expectedReturn,
    totalScore,
    riskAversionCoefficient,
    assetAllocation,
    apiMessage: apiResult.message || 'Asset allocation optimized successfully'
  };
};

// Generate mock asset allocation based on risk profile
const generateMockAllocation = (riskProfile, answers, totalScore, riskAversionCoefficient) => {
  const rawAllocations = {
    Conservative: [
      { name: 'Fixed Deposits', allocation: 40, risk: 'Low', description: 'Secure fixed-income instruments with guaranteed returns from banks and NBFCs.' },
      { name: 'Debt Mutual Funds', allocation: 30, risk: 'Low', description: 'Invest in fixed-income instruments to offer stable returns with lower risk than equity funds.' },
      { name: 'Large-Cap Equity', allocation: 15, risk: 'Low', description: 'Top 100 companies by market cap per SEBI, like Reliance, TCS, and Infosys offering stability.' },
      { name: 'Gold', allocation: 10, risk: 'Low', description: 'Precious metal investments for portfolio diversification and inflation protection.' },
      { name: 'Real Estate', allocation: 5, risk: 'Medium', description: 'Property investments including REITs for long-term appreciation and rental income.' }
    ],
    Moderate: [
      { name: 'Large-Cap Equity', allocation: 20, risk: 'Low', description: 'Top 100 companies by market cap per SEBI, like Reliance, TCS, and Infosys offering stability.' },
      { name: 'Debt Mutual Funds', allocation: 25, risk: 'Medium', description: 'Invest in fixed-income instruments to offer stable returns with lower risk than equity funds.' },
      { name: 'Flexi-cap Mutual Funds', allocation: 15, risk: 'Medium', description: 'Equity funds that invest across large-cap, mid-cap, and small-cap stocks without any restriction.' },
      { name: 'Mid-Cap Equity', allocation: 10, risk: 'Medium', description: 'Companies ranked 101 to 250 by market capitalisation, local and global leaders with higher growth potential.' },
      { name: 'Real Estate', allocation: 10, risk: 'Medium', description: 'Property investments including REITs for long-term appreciation and rental income.' },
      { name: 'Small-Cap Equity', allocation: 5, risk: 'High', description: 'Companies ranked below 250 by market capitalisation with high growth potential but higher volatility.' },
      { name: 'Fixed Deposits', allocation: 5, risk: 'Low', description: 'Secure fixed-income instruments with guaranteed returns from banks and NBFCs.' },
      { name: 'Gold', allocation: 5, risk: 'Low', description: 'Precious metal investments for portfolio diversification and inflation protection.' }
    ],
    Aggressive: [
      { name: 'Large-Cap Equity', allocation: 25, risk: 'Low', description: 'Top 100 companies by market cap per SEBI, like Reliance, TCS, and Infosys offering stability.' },
      { name: 'Mid-Cap Equity', allocation: 20, risk: 'Medium', description: 'Companies ranked 101 to 250 by market capitalisation, local and global leaders with higher growth potential.' },
      { name: 'Small-Cap Equity', allocation: 15, risk: 'High', description: 'Companies ranked below 250 by market capitalisation with high growth potential but higher volatility.' },
      { name: 'Flexi-cap Mutual Funds', allocation: 15, risk: 'Medium', description: 'Equity funds that invest across large-cap, mid-cap, and small-cap stocks without any restriction.' },
      { name: 'Real Estate', allocation: 10, risk: 'Medium', description: 'Property investments including REITs for long-term appreciation and rental income.' },
      { name: 'Debt Mutual Funds', allocation: 10, risk: 'Medium', description: 'Invest in fixed-income instruments to offer stable returns with lower risk than equity funds.' },
      { name: 'Gold', allocation: 3, risk: 'Low', description: 'Precious metal investments for portfolio diversification and inflation protection.' },
      { name: 'Fixed Deposits', allocation: 2, risk: 'Low', description: 'Secure fixed-income instruments with guaranteed returns from banks and NBFCs.' }
    ]
  };

  const investmentHorizon = getInvestmentHorizon(answers);
  const expectedReturn = getExpectedReturn(riskProfile);

  // Normalize allocations to ensure they sum to 100%
  const selectedAllocations = rawAllocations[riskProfile] || rawAllocations.Moderate;
  const normalizedAllocations = normalizeAllocations(selectedAllocations);

  return {
    clientName: clientData.value?.name || 'Client',
    clientEmail: clientData.value?.email || '',
    clientAge: clientData.value?.age || '',
    riskProfile,
    investmentHorizon,
    expectedReturn,
    totalScore,
    riskAversionCoefficient,
    assetAllocation: normalizedAllocations
  };
};

// Get investment horizon from answers
const getInvestmentHorizon = (answers) => {
  const timeframe = answers.investmentTimeframe?.value;
  switch(timeframe) {
    case 'lessThan1Year': return 'Short-Term';
    case '1to2Years': return 'Short-Term';
    case '3to5Years': return 'Medium-Term';
    case 'moreThan5Years': return 'Long-Term';
    default: return 'Long-Term';
  }
};

// Get expected return based on risk profile
const getExpectedReturn = (riskProfile) => {
  switch(riskProfile) {
    case 'Conservative': return '6-8% p.a.';
    case 'Moderate': return '10-12% p.a.';
    case 'Aggressive': return '14-16% p.a.';
    default: return '10-12% p.a.';
  }
};

// Handle reassess button
const handleReassess = () => {
  currentStep.value = 'questionnaire';
  isSubmitted.value = false;
  allocationData.value = null;

  // Scroll to top when going back to questionnaire
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};

// Handle back to client info
const handleBackToClientInfo = () => {
  currentStep.value = 'clientInfo';
  isSubmitted.value = false;
  allocationData.value = null;

  // Scroll to top when going back
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};

// Handle back to landing
const handleBackToLanding = () => {
  router.push('/');
};

// Set page title and scroll to top
onMounted(() => {
  document.title = 'Risk Assessment - AssetNivesh';
  // Scroll to top of the page when component mounts
  window.scrollTo({ top: 0, behavior: 'smooth' });
});
</script>

<template>
  <div class="risk-profile-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-container">
        <button @click="handleBackToLanding" class="back-button">
          ← Back to Home
        </button>
        <h1 class="page-title">Risk Profile Assessment</h1>
      </div>
    </div>

    <!-- Loading State -->
    <AnLoader v-if="isLoading" text="Processing your assessment..." />

    <!-- Main Content -->
    <main v-else class="page-content">
      <!-- Client Info Step -->
      <ClientInfoForm
        v-if="currentStep === 'clientInfo'"
        :onSubmit="handleClientInfoSubmit"
        :isSubmitted="false"
      />

      <!-- Questionnaire Step -->
      <RiskQuestionnaire
        v-else-if="currentStep === 'questionnaire'"
        :onSubmit="handleQuestionnaireSubmit"
        :onBack="handleBackToClientInfo"
        :isSubmitted="isSubmitted"
      />

      <!-- Results Step -->
      <AssetAllocationResult
        v-else-if="currentStep === 'results' && allocationData"
        :allocationData="allocationData"
        :isDemoMode="allocationData?.isDemoMode || false"
        :onReassess="handleReassess"
        :onBackToLanding="handleBackToLanding"
      />
    </main>
  </div>
</template>

<style scoped>
.risk-profile-page {
  min-height: 100vh;
  background-color: var(--an-background-light);
  font-family: "Satoshi-Variable", sans-serif;
}

.page-header {
  background: var(--an-white);
  box-shadow: var(--an-card-shadow);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--an-gray3);
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.back-button {
  padding: 12px 20px;
  background: var(--an-white);
  border: 2px solid var(--an-primary);
  border-radius: 8px;
  color: var(--an-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: var(--an-primary);
  color: var(--an-white);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.15);
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 50%, var(--an-tertiary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-content {
  padding: 32px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-container {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .page-content {
    padding: 20px 16px;
  }

  .back-button {
    align-self: flex-start;
  }
}
</style>
